import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Check, Copy, Code, ExternalLink, Plus, Trash2, AlertCircle } from 'lucide-react';
import { agentsAPI } from '../lib/api';

const EmbedCode = ({ agent }) => {
  const [copied, setCopied] = useState(false);
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState('');
  const [addingDomain, setAddingDomain] = useState(false);
  const [deletingDomain, setDeletingDomain] = useState(null);
  const [loading, setLoading] = useState(true);

  const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || window.location.origin;

  const loadDomains = useCallback(async () => {
    if (!agent?.agent_id) return;
    
    try {
      setLoading(true);
      const data = await agentsAPI.getDomains(agent.agent_id);
      setDomains(data.domains || []);
    } catch (error) {
      console.error('Error loading domains:', error);
      setDomains([]);
    } finally {
      setLoading(false);
    }
  }, [agent?.agent_id]);

  useEffect(() => {
    loadDomains();
  }, [loadDomains]);

  const handleAddDomain = async () => {
    if (!newDomain.trim() || addingDomain) return;
    
    setAddingDomain(true);
    try {
      await agentsAPI.addDomain(agent.agent_id, newDomain.trim());
      setNewDomain('');
      await loadDomains();
    } catch (error) {
      console.error('Error adding domain:', error);
      alert(error.response?.data?.detail || 'Failed to add domain');
    } finally {
      setAddingDomain(false);
    }
  };

  const handleDeleteDomain = async (domainId) => {
    if (deletingDomain) return;
    
    if (!window.confirm('Are you sure you want to remove this domain?')) {
      return;
    }
    
    setDeletingDomain(domainId);
    try {
      await agentsAPI.deleteDomain(agent.agent_id, domainId);
      await loadDomains();
    } catch (error) {
      console.error('Error deleting domain:', error);
      alert(error.response?.data?.detail || 'Failed to delete domain');
    } finally {
      setDeletingDomain(null);
    }
  };

  const iframeCode = `<iframe
  src="${FRONTEND_URL}/widget/${agent.agent_id}?mode=inline"
  style="width:100%;height:600px;border:0;border-radius:12px;overflow:hidden;"
  referrerpolicy="no-referrer-when-downgrade"
  loading="lazy"
></iframe>`;

  const copyToClipboard = async (text) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Domain Allowlist Manager (Prompt 10) */}
      <Card className="bg-slate-900/50 border-slate-800 lg:col-span-2">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white">Domain Allowlist</CardTitle>
              <CardDescription className="text-slate-400">
                Widget only works on allowed domains. Add domains where your widget will be embedded.
              </CardDescription>
            </div>
            <Badge 
              variant="outline" 
              className={agent.is_active ? 'border-emerald-500/50 text-emerald-400' : 'border-red-500/50 text-red-400'}
            >
              {agent.is_active ? 'Widget Active' : 'Widget Inactive'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add Domain */}
          <div className="flex gap-2">
            <Input
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="example.com (no http:// or www.)"
              className="bg-slate-800 border-slate-700 text-white"
              onKeyPress={(e) => e.key === 'Enter' && handleAddDomain()}
            />
            <Button 
              onClick={handleAddDomain}
              disabled={!newDomain.trim() || addingDomain}
              className="bg-indigo-600 hover:bg-indigo-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Domain
            </Button>
          </div>

          {/* Domains List */}
          {loading ? (
            <div className="text-slate-400 text-sm">Loading domains...</div>
          ) : domains.length === 0 ? (
            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-amber-400" />
                <div>
                  <p className="text-sm font-medium text-amber-200">No domains configured</p>
                  <p className="text-xs text-amber-400/80 mt-1">
                    Add at least one domain to enable the widget. Widget requests from other domains will be blocked.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Allowed Domains ({domains.length})</Label>
              <div className="space-y-2">
                {domains.map((domain) => (
                  <div 
                    key={domain.domain_id} 
                    className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700"
                  >
                    <div className="flex items-center gap-2">
                      <ExternalLink className="h-4 w-4 text-slate-400" />
                      <code className="text-sm text-slate-300">{domain.domain}</code>
                      {domain.enabled ? (
                        <Badge variant="outline" className="border-emerald-500/50 text-emerald-400 text-xs">
                          Enabled
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="border-slate-500/50 text-slate-400 text-xs">
                          Disabled
                        </Badge>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteDomain(domain.domain_id)}
                      disabled={deletingDomain === domain.domain_id}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* IFRAME Embed Code (Prompt 10) */}
      <Card className="bg-slate-900/50 border-slate-800 lg:col-span-2">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white">IFRAME Embed Code</CardTitle>
              <CardDescription className="text-slate-400">Add this iframe to your website's HTML</CardDescription>
            </div>
            {domains.length === 0 && (
              <Badge variant="outline" className="border-amber-500/50 text-amber-400">
                Add domains first
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <pre className="bg-slate-950 border border-slate-800 rounded-lg p-4 overflow-x-auto text-sm">
              <code className="text-slate-300">{iframeCode}</code>
            </pre>
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyToClipboard(iframeCode)}
              className="absolute top-2 right-2 border-slate-700 text-slate-400 hover:text-white"
            >
              {copied ? (
                <><Check className="h-4 w-4 mr-1 text-emerald-400" /> Copied!</>
              ) : (
                <><Copy className="h-4 w-4 mr-1" /> Copy</>
              )}
            </Button>
          </div>
          
          <div className="mt-4 p-4 bg-slate-800/50 rounded-lg">
            <h4 className="text-sm font-medium text-white mb-2">Installation Steps:</h4>
            <ol className="text-sm text-slate-400 space-y-2">
              <li className="flex gap-2">
                <span className="text-indigo-400 font-mono">1.</span>
                Add the domain where you'll embed the widget to the allowlist above
              </li>
              <li className="flex gap-2">
                <span className="text-indigo-400 font-mono">2.</span>
                Copy the iframe code above
              </li>
              <li className="flex gap-2">
                <span className="text-indigo-400 font-mono">3.</span>
                Paste it in your website's HTML where you want the widget to appear
              </li>
              <li className="flex gap-2">
                <span className="text-indigo-400 font-mono">4.</span>
                The widget will appear on your site (only works on allowed domains)
              </li>
            </ol>
          </div>

          {domains.length === 0 && (
            <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-amber-400" />
                <div>
                  <p className="text-sm font-medium text-amber-200">No domains configured</p>
                  <p className="text-xs text-amber-400/80 mt-1">
                    You must add at least one domain to the allowlist before the widget will work.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Widget Preview */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white text-base">Widget Preview</CardTitle>
          <CardDescription className="text-slate-400">How your widget will look</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative h-[200px] bg-slate-800/50 rounded-lg overflow-hidden">
            {/* Mock website background */}
            <div className="absolute inset-0 p-4">
              <div className="h-3 bg-slate-700 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-slate-700 rounded w-1/2 mb-4"></div>
              <div className="h-20 bg-slate-700/50 rounded"></div>
            </div>
            
            {/* Widget button */}
            <div 
              className={`absolute ${
                agent.widget_settings?.position === 'bottom-left' ? 'left-4' : 'right-4'
              } bottom-4`}
            >
              <div 
                className="w-14 h-14 rounded-full shadow-lg flex items-center justify-center cursor-pointer transition-transform hover:scale-110"
                style={{ backgroundColor: agent.widget_settings?.primary_color || '#6366f1' }}
              >
                <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
            </div>
          </div>
          
          <div className="mt-4 flex items-center justify-between">
            <span className="text-sm text-slate-400">Position: {agent.widget_settings?.position || 'bottom-right'}</span>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-400">Color:</span>
              <div 
                className="w-6 h-6 rounded border border-slate-600"
                style={{ backgroundColor: agent.widget_settings?.primary_color || '#6366f1' }}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmbedCode;
